import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AccaBoostConfig, CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';

import {
    AccaBoostToken,
    BetAndGetToken,
    FreeBetToken,
    IRewardToken,
    IRewardTokenInfo,
    OddsBoostToken,
    RewardTokenContext,
    RiskFreeToken,
} from '../../../reward-tokens.model';
import { RewardTokensBannerService } from '../../../services/reward-tokens-banner.service';
import { isAccaBoostToken, isBetAndGetToken, isFreebetToken, isOddsBoostToken, isRiskFreeToken } from '../../../services/reward-tokens.utils';

@Component({
    selector: 'bs-reward-tokens-card',
    templateUrl: './reward-tokens-card.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./reward-tokens-card.component.scss'],
    standalone: false,
})
export class RewardTokensCardComponent implements OnChanges, OnInit {
    @Input() tokenInfo: IRewardTokenInfo;
    @Input() isRewardBannerView = false;
    @Input() isRewardDrawerEnabled: boolean;
    @Input() tokenContext?: RewardTokenContext;
    @Output() cardClick = new EventEmitter<IRewardTokenInfo>();

    @HostBinding('class') class = 'reward-tokens-card reward-tokens-module';

    @HostBinding('class.reward-tokens-card--selected') get selected(): boolean {
        return this.tokenInfo.selected;
    }

    @HostBinding('class.reward-banner-view') get rewardBannerView(): boolean {
        return this.isRewardBannerView;
    }

    @HostBinding('class.reward-banner-token-card') get rewardDrawerEnabledToken(): boolean {
        return this.isRewardDrawerEnabled;
    }

    showBetNowEpcotNotification: boolean;
    iconUrl: string;
    rewardTokenType = RewardTokenType;
    rewardsIcons: { [id: string]: string } = {};
    rewardsToastMessage: string;
    isVipExclusive?: boolean;
    public readonly defaultVipBadgeIconSize: string = '23';
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private tokensConfig: CrmConfig,
        private accaBoostConfig: AccaBoostConfig,
        private rewardTokensBannerService: RewardTokensBannerService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnChanges(): void {
        this.rewardsIcons = this.rewardTokensBannerService.getIconUrl(this.isRewardBannerView);
        this.iconUrl = this.tokenInfo ? this.rewardsIcons[this.tokenInfo.token.rewardTokenType] : '';
        this.isVipExclusive = this.tokensConfig.tokens.isVIPBadgeEnabled && this.tokenInfo.token.isVipExclusive;
    }

    ngOnInit(): void {
        this.rewardTokensBannerService.recentlyClickedToken$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((userTokenId) => {
            this.showBetNowEpcotNotification = this.tokenInfo.token.userTokenId === userTokenId;
            this.rewardsToastMessage = this.sitecore.crmTokens.messages.RewardsToastMessage;
            this.cdr.detectChanges();
        });
    }

    get iconClasses(): string[] {
        if (isFreebetToken(this.tokenInfo.token) && this.tokensConfig.tokens.icons.freeBet) {
            return ['reward-tokens-card__icon--freebet', this.tokensConfig.tokens.icons.freeBet];
        }

        if (isOddsBoostToken(this.tokenInfo.token) && this.tokensConfig.tokens.icons.oddsBoost) {
            return ['reward-tokens-card__icon--odds-boost', this.tokensConfig.tokens.icons.oddsBoost];
        }

        if (isRiskFreeToken(this.tokenInfo.token)) {
            return ['reward-tokens-card__icon--risk-free', this.tokensConfig.tokens.icons.riskFree];
        }

        if (isBetAndGetToken(this.tokenInfo.token) && this.tokensConfig.tokens.icons.betAndGet) {
            return ['reward-tokens-card__icon--betandget', this.tokensConfig.tokens.icons.betAndGet];
        }

        if (isAccaBoostToken(this.tokenInfo.token)) {
            const iconClass = this.accaBoostConfig.isEnhancedLayoutEnabled
                ? 'reward-tokens-card__icon--acca-boost-enhanced'
                : 'reward-tokens-card__icon--acca-boost';

            return [iconClass, this.accaBoostConfig.icon];
        }

        return [];
    }

    hideToaster(): void {
        this.rewardTokensBannerService.setRecentlyClicked(null);
    }

    tokenSelected(): boolean {
        return this.tokenInfo.selected && this.tokensConfig.tokens.showApplyRemoveButtons;
    }

    @HostListener('click')
    onCardClick(): void {
        this.cardClick.next(this.tokenInfo);
    }

    isTokenType(token: IRewardToken, type: 'AccaBoost'): token is AccaBoostToken;
    isTokenType(token: IRewardToken, type: 'OddsBoost'): token is OddsBoostToken;
    isTokenType(token: IRewardToken, type: 'FreeBet'): token is FreeBetToken;
    isTokenType(token: IRewardToken, type: 'RiskFreeBet'): token is RiskFreeToken;
    isTokenType(token: IRewardToken, type: 'BetAndGet'): token is BetAndGetToken;
    isTokenType(token: IRewardToken, type: keyof typeof RewardTokenType): boolean {
        return token.rewardTokenType === RewardTokenType[type];
    }
}
